/**
 * The demo booking dialog: pick a language, then book inside the same dialog.
 *
 * 🔴 A HAND-ROLLED DIALOG, NOT the app's `Dialog` primitive — same reason
 * `TourLauncher` uses plain markup. This module imports nothing from the host
 * application, which is what lets the whole folder be deleted or lifted
 * elsewhere without chasing imports. A modal is Escape + a backdrop + focus
 * handling; that is cheaper than the coupling.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { restoreHostChrome, suppressHostChrome } from '../engine/hostChrome';
import { DEMO_OPTIONS, type DemoOption } from '../sandboxLinks';

export interface DemoBookingDialogProps {
  onClose(): void;
}

export function DemoBookingDialog({ onClose }: DemoBookingDialogProps) {
  const [chosen, setChosen] = useState<DemoOption | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // 🔴 The sandbox badge is pinned at `z-index: 2147483647`, the highest value a
    // stacking context can hold, so this dialog CANNOT be layered above it. On a
    // short viewport it lands exactly on the footer's buttons. Hiding it for the
    // duration — the same mechanism a running tour uses — is the only fix
    // available; raising the dialog is arithmetically impossible.
    const hidden = suppressHostChrome();

    // Focus goes back where it came from. Without the restore, closing drops focus
    // to the top of the document and a keyboard user loses their place entirely.
    const returnFocusTo = document.activeElement as HTMLElement | null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Capture + stop, so the key does not also reach a host-level handler and
        // close something behind this dialog at the same time.
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    // Focus the dialog itself rather than the first option: it carries the title,
    // so a screen reader announces what this is before what to do about it.
    //
    // Deliberately NOT a focus trap. Tab can reach the page behind — accepted,
    // because the booking view is a cross-origin iframe: once focus moves inside
    // it, `document.activeElement` is the frame element and nothing more, so a trap
    // has no way to tell "still in the booking form" from "escaped past it". A trap
    // that guesses would either fight the user's tabbing through the real form or
    // yank focus out of it. `aria-modal` still tells assistive tech what this is.
    dialogRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      restoreHostChrome(hidden);
      returnFocusTo?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="tour-demo-backdrop"
      data-testid="demo-booking-dialog"
      // Dismissal requires the press to START on the backdrop. `mousedown` rather
      // than `click` is what gives that: a drag begun inside the dialog and
      // released out here would satisfy `click`'s target rules on some engines, and
      // selecting text in the booking form must never dismiss the booking form.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="tour-demo-dialog"
        // Wider once the booking page is showing: the calendar needs the room, and
        // a dialog that big around two option cards looks broken.
        data-view={chosen ? 'booking' : 'choose'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-demo-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="tour-demo-head">
          <div>
            <h2 className="tour-demo-title" id="tour-demo-title">
              {chosen ? `Book a demo — ${chosen.label}` : 'Book a live demo'}
            </h2>
            <p className="tour-demo-subtitle">
              {chosen
                ? 'Pick a slot that suits you. You will get a confirmation by email.'
                : 'Which language would you like the demo in?'}
            </p>
          </div>
          <button
            type="button"
            className="tour-demo-close"
            onClick={onClose}
            aria-label="Close"
            data-testid="demo-booking-close"
          >
            ×
          </button>
        </header>

        {!chosen ? (
          <div className="tour-demo-options">
            {DEMO_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className="tour-demo-option"
                onClick={() => setChosen(option)}
                data-testid={`demo-option-${option.id}`}
              >
                <span className="tour-demo-option__label">{option.label}</span>
                <span className="tour-demo-option__desc">{option.description}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="tour-demo-frame-wrap">
            <iframe
              className="tour-demo-frame"
              src={chosen.bookingUrl}
              title={`Book a demo in ${chosen.label}`}
              data-testid="demo-booking-frame"
            />
          </div>
        )}

        <footer className="tour-demo-foot">
          {chosen ? (
            <button
              type="button"
              className="tour-demo-back"
              onClick={() => setChosen(null)}
              data-testid="demo-booking-back"
            >
              ‹ Change language
            </button>
          ) : (
            <span className="tour-demo-note">Takes about 20 minutes.</span>
          )}
          {/*
            🔴 ALWAYS OFFERED, never conditional on the frame having failed.
            A frame refused by the browser (a stricter third-party-frame setting, a
            deployment without the /book/ carve-out) fires no error event that could
            be detected — the area is simply blank. A visible escape hatch is the
            only honest way to keep the demo bookable in that case.
          */}
          {chosen && (
            <a
              className="tour-demo-newtab"
              href={chosen.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="demo-booking-newtab"
            >
              Open in a new tab ↗
            </a>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
